import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X, Heart, LogOut } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useWishlist } from "@/contexts/WishlistContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop All", to: "/shop" },
  { label: "Banarasi", to: "/shop?category=Banarasi" },
  { label: "Kanjivaram", to: "/shop?category=Kanjivaram" },
  { label: "Silk", to: "/shop?category=Silk" },
  { label: "New Arrivals", to: "/shop?new=1" },
];

export function Header() {
  const { count, setDrawerOpen } = useCart();
  const { user, logout } = useAuth();
  const { count: wishCount } = useWishlist();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#fcfbf8]/90 border-b border-[#e8e4db]"
      data-testid="site-header"
    >
      {/* Top strip */}
      <div className="bg-[#7a1e2b] text-[#fcfbf8] text-xs tracking-[0.18em] uppercase text-center py-2 font-medium">
        Complimentary shipping on orders over ₹2,000 · Grace in every drape
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-24 flex items-center justify-between gap-6">
        <button
          className="md:hidden text-[#2b1b1c]"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="mobile-menu-btn"
          aria-label="menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <Link to="/" data-testid="header-logo-link">
          <Logo size={64} />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              className="link-underline text-[13px] tracking-[0.14em] uppercase text-[#2b1b1c] hover:text-[#7a1e2b]"
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <button
            className="text-[#2b1b1c] hover:text-[#7a1e2b] transition-colors"
            onClick={() => navigate("/shop")}
            aria-label="search"
            data-testid="header-search-btn"
          >
            <Search size={20} />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="text-[#2b1b1c] hover:text-[#7a1e2b] transition-colors"
                data-testid="header-account-btn"
                aria-label="account"
              >
                <User size={20} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white w-56">
              {user ? (
                <>
                  <div className="px-3 py-2 text-sm">
                    <div className="font-medium truncate">{user.name}</div>
                    <div className="text-xs text-[#7c7273] truncate">{user.email}</div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-my-account">
                    My Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account?tab=orders")} data-testid="menu-orders">
                    Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/account?tab=wishlist")} data-testid="menu-wishlist">
                    Wishlist
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut size={14} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => navigate("/login")} data-testid="menu-login">
                    Sign in
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/signup")} data-testid="menu-signup">
                    Create account
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            className="relative text-[#2b1b1c] hover:text-[#7a1e2b] transition-colors hidden sm:block"
            onClick={() => navigate("/account?tab=wishlist")}
            data-testid="header-wishlist-btn"
            aria-label="wishlist"
          >
            <Heart size={20} />
            {wishCount > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#c9a34a] text-[#2b1b1c] text-[10px] font-medium flex items-center justify-center"
                data-testid="header-wishlist-count"
              >
                {wishCount}
              </span>
            )}
          </button>

          <button
            className="relative text-[#2b1b1c] hover:text-[#7a1e2b] transition-colors"
            onClick={() => setDrawerOpen(true)}
            data-testid="header-cart-btn"
            aria-label="cart"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#7a1e2b] text-[#fcfbf8] text-[10px] font-medium flex items-center justify-center"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[#e8e4db] bg-[#fcfbf8]">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                className="text-sm tracking-[0.14em] uppercase py-2 border-b border-[#e8e4db] text-[#2b1b1c]"
              >
                {n.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
